import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Track } from '../components/PageComponents/trackList/track';
import { Tracklist } from '../components/PageComponents/trackList/getAll';
import { UserContext } from './UserContext';
import { API_BASE } from '../config/api';
import { useNowPlayingSync, ConnectionStatus, NowPlayingSnapshot } from '../hooks/useNowPlayingSync';

export type RepeatMode = 'off' | 'all' | 'one';

interface PlayerContextType {
  currentSong: Track;
  setCurrentSong: (song: Track) => void;
  isPlaying: boolean;
  setIsPlaying: (state: boolean) => void;
  tracks: Track[];
  tracksLoading: boolean;
  refreshTracks: () => Promise<void>;
  shuffle: boolean;
  toggleShuffle: () => void;
  playNext: () => void;
  playPrevious: () => void;
  queue: Track[];
  queueIndex: number;
  setQueue: (tracks: Track[], startIndex?: number) => void;
  addToQueue: (track: Track) => void;
  addTracksToQueue: (tracks: Track[]) => void;
  playTracksNext: (tracks: Track[]) => void;
  reorderQueue: (newQueue: Track[]) => void;
  playAtQueueIndex: (index: number) => void;
  playTrackNow: (track: Track) => void;
  connectionStatus: ConnectionStatus;
  resumePositionSeconds: number | null;
  clearResumePosition: () => void;
  reportPlaybackPosition: (positionSeconds: number, playingOverride?: boolean) => void;
  currentTime: number;
  duration: number;
  togglePlay: () => void;
  seek: (time: number) => void;
  repeatMode: RepeatMode;
  setRepeatMode: (mode: RepeatMode) => void;
  cycleRepeat: () => void;
  volume: number;
  setVolume: (volume: number) => void;
}

export const PlayerContext = createContext<PlayerContextType>({
  currentSong: '' as unknown as Track,
  setCurrentSong: () => {},
  isPlaying: false,
  setIsPlaying: () => {},
  tracks: [],
  tracksLoading: true,
  refreshTracks: async () => {},
  shuffle: false,
  toggleShuffle: () => {},
  playNext: () => {},
  playPrevious: () => {},
  queue: [],
  queueIndex: -1,
  setQueue: () => {},
  addToQueue: () => {},
  addTracksToQueue: () => {},
  playTracksNext: () => {},
  reorderQueue: () => {},
  playAtQueueIndex: () => {},
  playTrackNow: () => {},
  connectionStatus: 'disconnected',
  resumePositionSeconds: null,
  clearResumePosition: () => {},
  reportPlaybackPosition: () => {},
  currentTime: 0,
  duration: 0,
  togglePlay: () => {},
  seek: () => {},
  repeatMode: 'all',
  setRepeatMode: () => {},
  cycleRepeat: () => {},
  volume: 0.8,
  setVolume: () => {},
});

const VOLUME_STORAGE_KEY = 'musiceer:volume';

function shuffleArray<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export const PlayerProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useContext(UserContext);

  const [currentSong, setCurrentSong] = useState<Track>(new Track(0, '', [], '', 0));
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tracksLoading, setTracksLoading] = useState<boolean>(true);
  const [shuffle, setShuffle] = useState<boolean>(false);
  const [originalQueue, setOriginalQueue] = useState<Track[] | null>(null);
  // 'all' matches this app's original (and still default) behavior: with no
  // repeat concept at all, playNext already wrapped the queue indefinitely
  // via modulo arithmetic - see playNext below. 'off' is new: it's the only
  // mode where reaching the end of the queue actually stops playback.
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('all');
  const [volume, setVolumeState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(VOLUME_STORAGE_KEY);
      return stored !== null ? Number(stored) : 0.8;
    } catch {
      return 0.8;
    }
  });

  const [queue, setQueueState] = useState<Track[]>([]);
  const [queueIndex, setQueueIndex] = useState<number>(-1);
  const [pendingQueueIds, setPendingQueueIds] = useState<number[] | null>(null);

  const [pendingSnapshot, setPendingSnapshot] = useState<NowPlayingSnapshot | null>(null);
  const [resumePositionSeconds, setResumePositionSeconds] = useState<number | null>(null);

  const refreshTracks = useCallback(async () => {
    setTracksLoading(true);
    const tracklist = new Tracklist();
    await tracklist.getTracksFromServer();
    setTracks(tracklist.getTracks());
    setTracksLoading(false);
  }, []);

  const persistQueue = useCallback((trackIds: number[]) => {
    fetch(`${API_BASE}/api/queue`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ track_ids: trackIds }),
    }).catch(() => {
      // Fire-and-forget: the queue still works locally even if this fails.
    });
  }, []);

  // Turning on: shuffles the queue and remembers the pre-shuffle order.
  // Turning off: restores that remembered order. Either way, playback isn't
  // interrupted - only queueIndex moves, to wherever the currently-playing
  // track landed in the new order.
  const toggleShuffle = useCallback(() => {
    const songId = currentSong?.getId();
    if (shuffle) {
      const restored = originalQueue ?? queue;
      setQueueState(restored);
      const idx = restored.findIndex((t) => t.getId() === songId);
      if (idx !== -1) setQueueIndex(idx);
      setOriginalQueue(null);
      setShuffle(false);
      persistQueue(restored.map((t) => t.getId()));
    } else {
      // The currently playing track leads the shuffled queue - only the
      // rest of the order is randomized - rather than landing at some
      // random position within it.
      const currentTrack = queue.find((t) => t.getId() === songId);
      const shuffledRest = shuffleArray(queue.filter((t) => t.getId() !== songId));
      const shuffled = currentTrack ? [currentTrack, ...shuffledRest] : shuffledRest;
      setOriginalQueue(queue);
      setQueueState(shuffled);
      setQueueIndex(currentTrack ? 0 : -1);
      setShuffle(true);
      persistQueue(shuffled.map((t) => t.getId()));
    }
  }, [shuffle, queue, originalQueue, currentSong, persistQueue]);

  // Points playback at a specific track and marks it playing. If that track
  // is already the one loaded (e.g. resuming something paused, or wrapping
  // a single-item queue back to itself), the streamUrl-driven load effect
  // further down won't re-fire - the URL isn't changing - so nothing else
  // would call audio.play(), and it'd stay silently paused despite
  // isPlaying flipping to true. Explicitly resuming here covers that case;
  // every path that jumps playback to a queue index goes through this.
  const activateTrack = useCallback(
    (index: number, track: Track) => {
      const alreadyLoaded = currentSong?.getId() === track.getId();
      setQueueIndex(index);
      setCurrentSong(track);
      setIsPlaying(true);
      if (alreadyLoaded) {
        audioRef.current?.play().catch(() => {});
      }
    },
    [currentSong]
  );

  const setQueue = useCallback(
    (newQueue: Track[], startIndex: number = 0) => {
      setPendingQueueIds(null);
      // A deliberate new queue (Play Album/Play All) supersedes any shuffle
      // in progress - there's no longer an "original order" to restore to.
      setShuffle(false);
      setOriginalQueue(null);
      setQueueState(newQueue);
      const clampedIndex = newQueue.length > 0 ? Math.min(Math.max(startIndex, 0), newQueue.length - 1) : -1;
      if (clampedIndex >= 0) {
        activateTrack(clampedIndex, newQueue[clampedIndex]);
      } else {
        setQueueIndex(-1);
      }
      persistQueue(newQueue.map((t) => t.getId()));
    },
    [persistQueue, activateTrack]
  );

  // Appends a track to the end of the queue without touching playback -
  // unlike playTrackNow, which always inserts right after the current track
  // and jumps to it immediately.
  const addToQueue = useCallback(
    (track: Track) => {
      setPendingQueueIds(null);
      const next = [...queue, track];
      setQueueState(next);
      persistQueue(next.map((t) => t.getId()));
      if (shuffle && originalQueue) {
        setOriginalQueue([...originalQueue, track]);
      }
    },
    [queue, persistQueue, shuffle, originalQueue]
  );

  // Bulk version of addToQueue - same append-at-end semantics, for an
  // album/artist/playlist's whole track list at once (the action row's
  // "Add to queue" button).
  const addTracksToQueue = useCallback(
    (newTracks: Track[]) => {
      if (newTracks.length === 0) return;
      setPendingQueueIds(null);
      const next = [...queue, ...newTracks];
      setQueueState(next);
      persistQueue(next.map((t) => t.getId()));
      if (shuffle && originalQueue) {
        setOriginalQueue([...originalQueue, ...newTracks]);
      }
    },
    [queue, persistQueue, shuffle, originalQueue]
  );

  // Bulk version of playTrackNow's "insert right after the current track"
  // placement, but - unlike playTrackNow - never touches currentSong/
  // isPlaying: "Play next" queues tracks up without interrupting whatever
  // is already playing.
  const playTracksNext = useCallback(
    (newTracks: Track[]) => {
      if (newTracks.length === 0) return;
      setPendingQueueIds(null);
      if (queue.length === 0) {
        setQueueState(newTracks);
        setQueueIndex(0);
        persistQueue(newTracks.map((t) => t.getId()));
      } else {
        const insertAt = queueIndex + 1;
        const next = [...queue.slice(0, insertAt), ...newTracks, ...queue.slice(insertAt)];
        setQueueState(next);
        persistQueue(next.map((t) => t.getId()));
      }
      if (shuffle && originalQueue) {
        setOriginalQueue([...originalQueue, ...newTracks]);
      }
    },
    [queue, queueIndex, persistQueue, shuffle, originalQueue]
  );

  // Reorders or removes items in the queue without interrupting whatever is
  // currently playing (unlike setQueue, which always jumps playback). Used
  // by the queue panel for both "move up/down" and "remove" - a shorter
  // newQueue means a removal, so mirror that removal onto the remembered
  // pre-shuffle order too (if shuffled) so un-shuffling later still makes
  // sense. A same-length reorder only affects the current shuffled view -
  // left out of the original order on purpose (best-effort, not synced).
  const reorderQueue = useCallback(
    (newQueue: Track[]) => {
      if (shuffle && originalQueue && newQueue.length < queue.length) {
        const remainingIds = new Set(newQueue.map((t) => t.getId()));
        setOriginalQueue(originalQueue.filter((t) => remainingIds.has(t.getId())));
      }
      setQueueState(newQueue);
      const newIndex = newQueue.findIndex((t) => t.getId() === currentSong?.getId());
      setQueueIndex(newIndex);
      persistQueue(newQueue.map((t) => t.getId()));
    },
    [currentSong, persistQueue, shuffle, originalQueue, queue]
  );

  // Jumps playback to an existing position within the current queue (e.g.
  // clicking a row in the queue panel) - unlike setQueue, this doesn't
  // replace the queue itself, just moves the "currently playing" cursor.
  const playAtQueueIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= queue.length) return;
      activateTrack(index, queue[index]);
    },
    [queue, activateTrack]
  );

  // Playing an arbitrary track (from the library grid, an album/artist
  // track list, search, etc.). If it's already somewhere in the queue -
  // e.g. clicking a later track of the album you're currently playing -
  // this just jumps the cursor to that existing spot instead of adding a
  // duplicate. Otherwise it inserts a new entry right after whatever's
  // currently playing and jumps to it. Either way, all playback goes
  // through the queue.
  const playTrackNow = useCallback(
    (track: Track) => {
      const existingIndex = queue.findIndex((t) => t.getId() === track.getId());
      if (existingIndex !== -1) {
        playAtQueueIndex(existingIndex);
        return;
      }

      setPendingQueueIds(null);
      if (queue.length === 0) {
        setQueueState([track]);
        setQueueIndex(0);
        persistQueue([track.getId()]);
      } else {
        const insertAt = queueIndex + 1;
        const next = [...queue.slice(0, insertAt), track, ...queue.slice(insertAt)];
        setQueueState(next);
        setQueueIndex(insertAt);
        persistQueue(next.map((t) => t.getId()));
      }
      // While shuffled, keep the remembered pre-shuffle order in sync too -
      // appended at the end, so it's still there (at the end) once unshuffled.
      if (shuffle && originalQueue) {
        setOriginalQueue([...originalQueue, track]);
      }
      setCurrentSong(track);
      setIsPlaying(true);
    },
    [queue, queueIndex, persistQueue, playAtQueueIndex, shuffle, originalQueue]
  );

  // Restore the saved queue once the user is known and the full track list
  // has loaded (queue entries are IDs, resolved against `tracks`).
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/queue`, { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        setPendingQueueIds(data.track_ids ?? []);
      } catch {
        // No saved queue, or the request failed - queue just stays empty.
      }
    })();
  }, [user]);

  useEffect(() => {
    if (pendingQueueIds === null || tracks.length === 0) return;
    const resolved = pendingQueueIds
      .map((id) => tracks.find((t) => t.getId() === id))
      .filter((t): t is Track => Boolean(t));
    setQueueState(resolved);
    setPendingQueueIds(null);
  }, [pendingQueueIds, tracks]);

  const handleSnapshot = useCallback((snapshot: NowPlayingSnapshot) => {
    setPendingSnapshot(snapshot);
  }, []);

  const { connectionStatus, reportState } = useNowPlayingSync(Boolean(user), handleSnapshot);

  // Set right before setCurrentSong during snapshot restore, so the
  // track-changed report effect below skips its usual "new track, report
  // position 0" report for this one - we're re-hydrating an already-current
  // position from the server, not starting fresh, and reporting 0 here
  // would immediately overwrite the very position we just resumed from.
  const suppressNextTrackReportRef = useRef(false);

  // True once the very first snapshot of this session has been processed
  // (applied or not). Distinguishes that first snapshot - fresh page
  // load/login, nothing local to resume from yet, so the server's saved
  // state is trusted - from every later one, which means the WebSocket
  // reconnected mid-session (most likely the server itself went down and
  // came back, since /api/stream and /api/ws/nowplaying are the same
  // process). By then we've been playing locally the whole time and know
  // the real position better than the now-stale saved row does.
  const sessionResumedRef = useRef(false);

  // Resolve the resume snapshot once the track list has loaded.
  useEffect(() => {
    if (!pendingSnapshot || tracks.length === 0) return;

    if (!sessionResumedRef.current) {
      sessionResumedRef.current = true;
      if (pendingSnapshot.trackId !== null) {
        const track = tracks.find((t) => t.getId() === pendingSnapshot.trackId);
        if (track) {
          suppressNextTrackReportRef.current = true;
          setCurrentSong(track);
          setResumePositionSeconds(pendingSnapshot.positionSeconds);
          setIsPlaying(pendingSnapshot.isPlaying);
        }
      }
    } else {
      // A reconnect mid-session - correct the server with where we actually
      // are instead of adopting its stale snapshot, and force the <audio>
      // element to drop its (almost certainly dead) buffered connection and
      // re-fetch from near the current position.
      reportPlaybackPosition(audioRef.current?.currentTime ?? 0, isPlayingRef.current);
      rebufferFromCurrentPosition();
    }

    setPendingSnapshot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSnapshot, tracks]);

  // Self-healing: the queue (from GET /api/queue) and currentSong (from the
  // WS resume snapshot) are restored independently after a page reload,
  // neither one sets queueIndex. Keep it in sync with wherever currentSong
  // actually sits in the queue - a no-op once they already agree, which
  // covers every other path that already sets both atomically.
  useEffect(() => {
    const songId = currentSong?.getId();
    if (!songId || queue.length === 0) return;
    if (queue[queueIndex]?.getId() === songId) return;
    const idx = queue.findIndex((t) => t.getId() === songId);
    if (idx !== -1) setQueueIndex(idx);
  }, [queue, currentSong, queueIndex]);

  const clearResumePosition = useCallback(() => setResumePositionSeconds(null), []);

  // Report playback state immediately whenever the track changes (a new
  // track always starts at position 0). Play/pause toggles report through
  // reportPlaybackPosition instead, since only AudioPlayer knows the real
  // current position at that moment - reporting 0 here on every isPlaying
  // flip would zero out the saved position each time the user pauses.
  const lastReportedRef = useRef<number>(0);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  useEffect(() => {
    const songId = currentSong?.getId();
    if (!songId) return;
    if (suppressNextTrackReportRef.current) {
      suppressNextTrackReportRef.current = false;
      return;
    }
    reportState(songId, 0, isPlayingRef.current);
    lastReportedRef.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSong]);

  // Exposed to AudioPlayer, which owns the actual <audio> element and is
  // the only place the live playback position is available. Used both for
  // the periodic heartbeat while playing, and for immediate pause/resume
  // reports - playingOverride lets the caller report the state it's about
  // to transition to, since context's own isPlaying hasn't updated yet at
  // the moment of a toggle.
  const reportPlaybackPosition = useCallback(
    (positionSeconds: number, playingOverride?: boolean) => {
      const songId = currentSong?.getId();
      if (!songId) return;
      reportState(songId, positionSeconds, playingOverride ?? isPlaying);
      lastReportedRef.current = Date.now();
    },
    [currentSong, isPlaying, reportState]
  );

  // All playback goes through the queue now (playTrackNow seeds it even for
  // a single arbitrary track), so there's no library-wide fallback anymore -
  // an empty queue just means there's nothing to skip to. Shuffle is baked
  // into the queue's actual order at toggle time (see toggleShuffle), so
  // these just walk it linearly regardless of whether it's shuffled.
  // repeatMode only changes what happens at the *last* track: 'off' stops
  // instead of wrapping back to index 0 (whether reached by the track
  // ending naturally or the skip-forward button) - every other step always
  // wraps, matching a manual skip's usual feel regardless of repeat mode.
  const playNext = useCallback(() => {
    if (queue.length === 0) return;
    if (queueIndex === queue.length - 1 && repeatMode === 'off') {
      setIsPlaying(false);
      return;
    }
    const nextIndex = (queueIndex + 1) % queue.length;
    activateTrack(nextIndex, queue[nextIndex]);
  }, [queue, queueIndex, repeatMode, activateTrack]);

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return;
    const prevIndex = (queueIndex - 1 + queue.length) % queue.length;
    activateTrack(prevIndex, queue[prevIndex]);
  }, [queue, queueIndex, activateTrack]);

  // The <audio> element and its playback lifecycle live here - rendered once
  // for the app's whole lifetime - rather than inside a per-page component.
  // Pages used to each mount their own <AudioPlayer>, so navigating between
  // pages unmounted/remounted the <audio> tag and restarted the track from
  // the beginning.
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const streamUrl = currentSong?.getId() ? `${API_BASE}/api/stream?id=${currentSong.getId()}` : '';

  const resumePositionRef = useRef(resumePositionSeconds);
  resumePositionRef.current = resumePositionSeconds;
  const playNextRef = useRef(playNext);
  playNextRef.current = playNext;
  // Tracks the last percent-of-duration we reported at, so onTimeUpdate
  // (which fires several times a second) doesn't spam the server - only
  // reports on crossing another whole percent. Reset per track below.
  const lastReportedPercentRef = useRef(0);

  // Depends only on streamUrl - reading isPlaying/resumePosition/playNext via
  // refs keeps a play/pause toggle or queue change from re-triggering this
  // (which would reload the file and reset playback).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !streamUrl) return;

    lastReportedPercentRef.current = 0;

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      if (resumePositionRef.current !== null) {
        audio.currentTime = resumePositionRef.current;
        clearResumePosition();
      }
    };
    // Reports progress every 1% of the track's duration instead of on a
    // fixed timer - self-scaling (frequent for a short song, sparser for a
    // long podcast) and keeps the saved position from ever drifting far
    // behind, which matters now that a reconnect trusts this saved position
    // as a starting point for a stalled stream to re-buffer from.
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        const percent = (audio.currentTime / audio.duration) * 100;
        if (percent - lastReportedPercentRef.current >= 1) {
          lastReportedPercentRef.current = percent;
          reportPlaybackPosition(audio.currentTime, true);
        }
      }
    };
    const onEnded = () => playNextRef.current();

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    audio.load();
    if (isPlayingRef.current) {
      audio.play().catch(() => {});
    }

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamUrl, clearResumePosition]);

  // Forces the <audio> element to drop whatever it has buffered and
  // re-fetch from near the current position - used when a WebSocket
  // reconnect implies the underlying /api/stream connection died too (same
  // backend process). Reseeks 5s before the current position rather than
  // exactly at it, so there's a small pre-roll buffer instead of a
  // hard cut the instant playback resumes.
  const rebufferFromCurrentPosition = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const resumeAt = Math.max(0, audio.currentTime - 5);
    resumePositionRef.current = resumeAt;
    setResumePositionSeconds(resumeAt);
    audio.load();
    if (isPlayingRef.current) audio.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else audio.play();
    setIsPlaying(!isPlaying);
    reportPlaybackPosition(audio.currentTime, !isPlaying);
  }, [isPlaying, reportPlaybackPosition]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
  }, []);

  // Cycles off -> all -> one -> off. 'one' uses the native <audio>.loop flag
  // below - while true, the browser restarts the same file on end and never
  // fires 'ended' at all, so onEnded's playNext() (which handles 'off'/'all')
  // correctly never runs for it.
  const cycleRepeat = useCallback(() => {
    setRepeatMode((m) => (m === 'off' ? 'all' : m === 'all' ? 'one' : 'off'));
  }, []);
  useEffect(() => {
    if (audioRef.current) audioRef.current.loop = repeatMode === 'one';
  }, [repeatMode]);

  const setVolume = useCallback((next: number) => {
    const clamped = Math.min(1, Math.max(0, next));
    setVolumeState(clamped);
    if (audioRef.current) audioRef.current.volume = clamped;
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
    } catch {
      // No localStorage (private mode, etc.) - volume just won't persist.
    }
  }, []);
  // Applies the stored/current volume once the <audio> element exists, and
  // again on streamUrl changes - a fresh <source> load shouldn't be audible
  // at some other, unapplied level even briefly.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamUrl]);

  return (
    <PlayerContext.Provider
      value={{
        currentSong,
        setCurrentSong,
        isPlaying,
        setIsPlaying,
        tracks,
        tracksLoading,
        refreshTracks,
        shuffle,
        toggleShuffle,
        playNext,
        playPrevious,
        queue,
        queueIndex,
        setQueue,
        addToQueue,
        addTracksToQueue,
        playTracksNext,
        reorderQueue,
        playAtQueueIndex,
        playTrackNow,
        connectionStatus,
        resumePositionSeconds,
        clearResumePosition,
        reportPlaybackPosition,
        currentTime,
        duration,
        togglePlay,
        seek,
        repeatMode,
        setRepeatMode,
        cycleRepeat,
        volume,
        setVolume,
      }}
    >
      <audio ref={audioRef}>
        <source src={streamUrl} type="audio/flac" />
      </audio>
      {children}
    </PlayerContext.Provider>
  );
};
